import { useCallback, useEffect, useState } from "react";
import { Truck, ExternalLink, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toISOString().replace("T", " ").slice(0, 16) + " UTC"; }
  catch { return iso; }
}

export const TrackingPanel = ({ refreshing }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ order_number: "", carrier: "usps", tracking_number: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try { setRows((await api.get("/admin/order-tracking")).data.tracking || []); }
    catch { setError("Couldn't load tracking entries. Please try again."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (!refreshing) load(); }, [refreshing, load]);

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.order_number.trim() || !form.tracking_number.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/order-tracking", {
        order_number: form.order_number.trim(),
        carrier: form.carrier,
        tracking_number: form.tracking_number.trim(),
      });
      toast.success("Tracking saved");
      setForm({ order_number: "", carrier: "usps", tracking_number: "" });
      load();
    } catch {
      toast.error("Could not save tracking. Check the details and try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (orderNumber) => {
    try {
      await api.delete(`/admin/order-tracking/${encodeURIComponent(orderNumber)}`);
      setRows((rs) => rs.filter((r) => r.order_number !== orderNumber));
    } catch { toast.error("Could not remove tracking."); }
  };

  return (
    <section className="space-y-8" data-testid="tracking-panel">
      <div className="card-soft rounded-2xl p-6" data-testid="tracking-form-card">
        <span className="eyebrow flex items-center gap-2"><Truck size={14} /> Fulfillment</span>
        <h2 className="font-display font-bold text-lg mt-2">Add shipping tracking</h2>
        <p className="text-sm text-muted-foreground mt-1">Enter an order number (e.g. ETH-XXXXXXXX or a WooCommerce order #) with its carrier and tracking number. Customers then see a live "Track" link on their order.</p>
        <form onSubmit={save} className="grid sm:grid-cols-4 gap-3 mt-5" data-testid="tracking-form">
          <Input placeholder="Order number" value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} className="h-11 bg-white" data-testid="tracking-order-input" required />
          <select value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} className="h-11 rounded-md border border-input bg-white px-3 text-sm" data-testid="tracking-carrier-select">
            <option value="usps">USPS</option>
            <option value="ups">UPS</option>
          </select>
          <Input placeholder="Tracking number" value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} className="h-11 bg-white" data-testid="tracking-number-input" required />
          <Button type="submit" disabled={saving} className="h-11 rounded-xl" data-testid="tracking-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </form>
      </div>

      <div className="card-soft rounded-2xl overflow-hidden" data-testid="tracking-list-card">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h3 className="font-display font-bold text-base">Tracked orders</h3>
          <Button variant="outline" onClick={load} disabled={loading} className="rounded-xl h-10 px-4" data-testid="tracking-refresh">Refresh</Button>
        </div>
        {error ? (
          <div className="p-6" role="alert" data-testid="tracking-error"><p className="text-sm text-destructive">{error}</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tracking-table">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-[#f6f1e6]">
                <th className="px-6 py-3 font-bold">Order</th>
                <th className="px-6 py-3 font-bold">Carrier</th>
                <th className="px-6 py-3 font-bold">Tracking #</th>
                <th className="px-6 py-3 font-bold hidden sm:table-cell">Updated (UTC)</th>
                <th className="px-6 py-3"></th>
              </tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="px-6 py-8 text-muted-foreground" data-testid="tracking-loading">Loading…</td></tr>
                ) : !rows.length ? (
                  <tr><td colSpan={5} className="px-6 py-10 text-center text-muted-foreground" data-testid="tracking-empty">No tracking added yet.</td></tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.order_number} data-testid={`tracking-row-${r.order_number}`} className="border-b border-border last:border-0 hover:bg-[#f9f6ef] transition-colors">
                      <td className="px-6 py-4 font-semibold">{r.order_number}</td>
                      <td className="px-6 py-4">{r.carrier_label}</td>
                      <td className="px-6 py-4">
                        <a href={r.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold hover:underline break-all" style={{ color: "var(--gold)" }} data-testid={`tracking-link-${r.order_number}`}>
                          {r.tracking_number} <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                        </a>
                      </td>
                      <td className="px-6 py-4 hidden sm:table-cell text-muted-foreground whitespace-nowrap">{fmt(r.updated_at)}</td>
                      <td className="px-6 py-4 text-right"><button onClick={() => remove(r.order_number)} className="text-muted-foreground hover:text-destructive" aria-label="Remove tracking" data-testid={`tracking-remove-${r.order_number}`}><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};
